import { OUT_OF_SESSION } from '../../constants';

/**
 * How an issue-reporting id is shortened for display.
 *
 * The ids on screen are uuidv4s, whose first segment is 8 hex characters — long
 * enough to pick one report out of a shift, short enough to sit in a row next to two
 * others. The full value is always one hover away and one click from the clipboard,
 * because the reason to show an id at all is cross-referencing with support and with
 * the logs, and that needs the whole thing.
 *
 * SPLIT ON THE HYPHEN rather than `slice(0, 8)`. Identical for a uuidv4, but it
 * degrades safely: if an id ever stops being a uuid, this shows a meaningful prefix or
 * the whole value instead of an arbitrary 8-character truncation that looks like a
 * uuid segment and is not one.
 *
 * Shared by the panel header (HITL-1573) and the backlog rows (HITL-1574), which must
 * shorten ids the same way or the same report reads as two different ones.
 */
export const shortId = (value: string | null | undefined): string =>
  value ? value.split('-')[0] : '';

/**
 * Whether a frozen `session_id` names a real session.
 *
 * A flag filed with no car present still gets a session id, because the last rung of
 * `resolveIssueReportSessionId` is `messages.currentSessionId`, which is SEEDED with
 * the `OUT_OF_SESSION` sentinel rather than a uuid. So the value can be `null`, or the
 * literal string `'out of session'` — which has no hyphen, and would therefore render
 * whole where every other row shows eight characters.
 *
 * Both cases mean the same thing to the agent and are shown the same way, rather than
 * leaking a sentinel into a column of ids.
 */
export const isRealSessionId = (value: string | null | undefined): boolean =>
  Boolean(value) && value !== OUT_OF_SESSION;

/**
 * How long ago a report was filed, for the trailing `(4m)` on a backlog row.
 *
 * Coarse on purpose — `4m`, not `4m 14s`. The agent is answering "which part of my
 * shift was this?", where the useful resolution is minutes. Seconds below a minute
 * because a report filed moments ago is one they still remember.
 *
 * No "ago" suffix: the row wraps this in brackets, where a preposition reads as
 * clutter. Takes `nowMs` rather than calling `Date.now()` itself, so the ages hold
 * still while the agent reads the list and the whole thing is testable without fake
 * timers.
 */
export const formatElapsed = (createdAt: string, nowMs: number): string => {
  const elapsedMs = nowMs - Date.parse(createdAt);
  // A record stamped in the future is a clock skew, not a negative age.
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return 'now';
  const seconds = Math.floor(elapsedMs / 1000);
  if (seconds < 5) return 'now';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h`;
};
